import { useEffect, useState } from "react";
import { afterLogin, joinDestination, rememberJoin } from "@/lib/authDestination";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import Wordmark from "@/components/Wordmark";
import Footer from "@/components/Footer";
import { Loader2, Mail, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function errText(detail, fallback = "Something went wrong. Please try again.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || "").filter(Boolean).join(" ") || fallback;
  return detail?.msg || fallback;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading, checkAuth } = useAuth();
  useEffect(() => {
    const target = new URLSearchParams(window.location.search).get("return_to");
    if (target) rememberJoin(target);
    if (!loading && user) window.location.replace(afterLogin(user));
  }, [user, loading]);
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "offline",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const devLogin = async () => {
    await api.post("/auth/demo-login");
    window.location.href = "/";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email });
        toast.success(data.message || "Reset link sent if that email has an account.");
        setMode("signin");
      } else {
        const path = mode === "signup" ? "/auth/register" : "/auth/login";
        await api.post(path, mode === "signup" ? { email, password, name } : { email, password });
        const current = await checkAuth();
        window.location.href = afterLogin(current);
      }
    } catch (e2) {
      setErr(errText(e2?.response?.data?.detail));
    }
    setBusy(false);
  };

  const disabled = busy;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="flex-1 flex flex-col lg:flex-row">
        <div className="hidden lg:flex flex-col justify-between w-1/2 border-r border-border p-12 bg-surface">
          <Wordmark size="text-2xl" logoClass="w-7 h-7" />
          <div>
            <h1 className="font-heading text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]">
              One shared view<br />for the entire<br />performance team.
            </h1>
            <p className="mt-6 text-muted-foreground max-w-md">
              Readiness, sharpness, effort and rest · measured against each athlete's own baseline. No spreadsheets. No disconnected reports.
            </p>
          </div>
          <div className="flex gap-6 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
            <span className="flex items-center gap-2"><span className="w-2 h-2 bg-traffic-green" /> Above baseline</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 bg-traffic-red" /> Below baseline</span>
          </div>
        </div>

        <div className="flex-1 flex items-start sm:items-center justify-center p-5 sm:p-8">
          <div className="w-full max-w-sm py-6">
            <div className="lg:hidden mb-8"><Wordmark size="text-2xl" logoClass="w-7 h-7" /></div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
              {mode === "signup" ? "Create account" : mode === "forgot" ? "Reset password" : "Sign in"}
            </p>
            <h2 className="font-heading text-lg font-black tracking-tight mt-2 mb-6">{mode === "signup" ? "Your next chapter starts here." : mode === "forgot" ? "Let's get you back in." : "Welcome back."}</h2>

            <p className="text-xs text-muted-foreground leading-relaxed mb-5" data-testid="auth-privacy-note">Your workspace is private to you and your authorised team. Athletes choose their data-sharing consent after joining. <Link to="/privacy" data-testid="dpdp-privacy-link" className="underline">Privacy policy</Link></p>
            {joinDestination() && <p className="mb-5 border-l-2 border-brand-magenta pl-3 text-sm" data-testid="login-invite-context">Your invitation is saved. Sign in or create an account to continue joining.</p>}

            <form onSubmit={submit} className="space-y-3" data-testid="email-auth-form">
              {mode === "signup" && (
                <input data-testid="signup-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Your name" className="inp" autoComplete="name" />
              )}
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@academy.com" className="inp pl-9" autoComplete="email" />
              </div>
              {mode !== "forgot" && (
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                  <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === "signup" ? "At least 8 characters" : "Password"} className="inp pl-9"
                    autoComplete={mode === "signup" ? "new-password" : "current-password"} />
                </div>
              )}
              {err && <p data-testid="login-error" className="text-xs text-traffic-red">{err}</p>}
              <button data-testid="email-submit-btn" type="submit" disabled={disabled}
                className="w-full bg-foreground text-background font-medium py-3 flex items-center justify-center gap-2 hover:bg-neutral-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
              </button>
            </form>

            <div className="flex items-center justify-between mt-3 text-xs">
              {mode === "forgot" ? (
                <button data-testid="back-to-signin" onClick={() => { setMode("signin"); setErr(""); }}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to sign in</button>
              ) : (
                <button data-testid="forgot-password-btn" onClick={() => { setMode("forgot"); setErr(""); }}
                  className="text-muted-foreground hover:text-foreground">Forgot password?</button>
              )}
              <button data-testid="toggle-signup" onClick={() => { setMode(mode === "signup" ? "signin" : "signup"); setErr(""); }}
                className="text-muted-foreground hover:text-foreground">
                {mode === "signup" ? "Have an account? Sign in" : "New here? Create account"}
              </button>
            </div>

            <div className="flex items-center gap-3 my-6">
              <span className="flex-1 h-px bg-border" />
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">or</span>
              <span className="flex-1 h-px bg-border" />
            </div>

            <button data-testid="google-login-btn" onClick={googleLogin} disabled={busy}
              className="w-full border border-border py-3 flex items-center justify-center gap-3 text-sm font-medium hover:bg-surface transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M21.35 11.1h-9.17v2.98h5.27c-.23 1.4-1.6 4.1-5.27 4.1-3.17 0-5.76-2.62-5.76-5.85s2.59-5.85 5.76-5.85c1.81 0 3.02.77 3.71 1.43l2.53-2.44C16.9 3.7 14.77 2.7 12.18 2.7 6.98 2.7 2.76 6.92 2.76 12.13s4.22 9.43 9.42 9.43c5.44 0 9.04-3.82 9.04-9.2 0-.62-.07-1.09-.17-1.56z" /></svg>
              Continue to Trifit.XPS
            </button>

            <button data-testid="dev-login-btn" onClick={devLogin}
              className="mt-3 w-full border border-dashed border-border text-sm py-2.5 text-muted-foreground hover:text-foreground hover:border-foreground transition-colors">
              Demo
            </button>
            <p className="text-[11px] text-muted-foreground mt-2 text-center">Demo is a synthetic sandbox · no real athlete data is reachable.</p>
            <p className="text-[11px] text-muted-foreground mt-3 text-center">
              Coaching a real squad?{" "}
              <Link to="/membership" data-testid="login-join-link" className="text-foreground font-medium underline underline-offset-2">See membership &amp; join</Link>
            </p>
          </div>
        </div>
      </div>
      <Footer compact />
    </div>
  );
}
